import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  filter,
  from,
  map,
  take,
  tap,
  throwError,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CookieWrapper } from './cookie.service';
import { environment } from '@environments/environment';

interface RefreshResponse {
  token: string;
  refresh_token: string;
}

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  private readonly cookie = inject(CookieWrapper);

  readonly ACCESS_TOKEN_COOKIE = 'portage_token';
  private readonly REFRESH_TOKEN_COOKIE = 'portage_refresh_token';
  private readonly ACCESS_TOKEN_EXPIRY_DAYS = 1;
  private readonly REFRESH_TOKEN_EXPIRY_DAYS = 7;

  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  storeTokens(token: string, refreshToken: string): void {
    this.cookie.set(
      this.ACCESS_TOKEN_COOKIE,
      token,
      this.ACCESS_TOKEN_EXPIRY_DAYS,
      '/'
    );
    this.cookie.set(
      this.REFRESH_TOKEN_COOKIE,
      refreshToken,
      this.REFRESH_TOKEN_EXPIRY_DAYS,
      '/'
    );
  }

  clearTokens(): void {
    this.cookie.delete(this.ACCESS_TOKEN_COOKIE, '/');
    this.cookie.delete(this.REFRESH_TOKEN_COOKIE, '/');
  }

  getAccessToken(): string | null {
    return this.cookie.get(this.ACCESS_TOKEN_COOKIE);
  }

  getRefreshToken(): string | null {
    return this.cookie.get(this.REFRESH_TOKEN_COOKIE);
  }

  /**
   * Rafraîchit l'access token via le refresh token.
   * Déduplique les appels concurrents : si un refresh est en cours,
   * les autres abonnés attendent le résultat du premier.
   * Utilise fetch (pas HttpClient) pour rester hors de l'intercepteur.
   */
  refreshAccessToken(): Observable<string> {
    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter((token): token is string => token !== null),
        take(1)
      );
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      return throwError(() => new Error('No refresh token available'));
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return from(
      fetch(`${environment.portageUrl}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).then(async response => {
        if (!response.ok) {
          throw new Error(`Refresh failed: ${response.status}`);
        }
        return response.json() as Promise<RefreshResponse>;
      })
    ).pipe(
      tap(res => {
        this.storeTokens(res.token, res.refresh_token);
        this.isRefreshing = false;
        this.refreshSubject.next(res.token);
      }),
      map(res => res.token),
      catchError(err => {
        this.isRefreshing = false;
        this.clearTokens();
        return throwError(() => err);
      })
    );
  }
}
